import React, { useEffect, useState } from 'react'
//components
import { Button } from '../../../../components/ui/buttons/Button'
import Label from '../../../../components/ui/labels/Label'
import { Input } from '../../../../components/ui/inputs/Input'
import { TextInput } from '../../../../components/ui/inputs/TextInput'
import { InputContainer } from '../../../../components/ui/inputs/InputContainers'
import Select from 'react-select'
import { SelectContainer } from '../../../../components/ui/layout/SelectContainer'
//hooks
import { useLoading } from '../../../../contexts/LoadingContext'
import useUserOptions from '../../../../hooks/useUserOptions'
import useUserOptionsGroups from '../../../../hooks/useUserOptionsGroups'
import { useError } from '../../../../contexts/ErrorContext'
import { useSuccess } from '../../../../contexts/SuccessContext'
//api
import { createPermissionGroupAPI, updateGroupDetailsAPI, assignPermissionGroupAPI, removePermissionGroupAPI } from '../../../../services/groupsAPI'

/**
 * 
 * Can add add a new group or update an existing one
 * 
 * Future task: could make this more dynamic in case an admin wants to change the pages a group can see
 * 
 * @param {Object | null} groupDetails - contains Group_Name and Description
 * @param {function} setSelectedGroup - function to set selected group in parent when new group created
 * @param {function} setGroupNameOptions - function to set group name options for dropdown in parent, when a new group added
 * @param {Boolean} isAddNewGroup - boolean that determines whether the page should show details of selected group, or inputs for adding a new group
 * @param {function} setIsAddNewGroup - function to set isAddNewGroup variable
 * 
 * @returns {JSX.Element}
 * 
 * @category Components - Admin Dashboard
 */

export const GroupDetailsForm = ({groupDetails = null, setSelectedGroup, setGroupNameOptions, isAddNewGroup, setIsAddNewGroup}) => {
    //set state
    const [groupName, setGroupName] = useState("")
    const [groupDescription, setGroupDescription] = useState("")
    
    const [usersAssigned, setUsersAssigned] = useState(null)
    const [usersAssignedOriginal, setUsersAssignedOriginal] = useState(null)
    
    const [validationErrors, setValidationErrors] = useState({})
    const {showLoading, hideLoading}  = useLoading()
    const {userNameOptions, fetchAllUsers} = useUserOptions()
    const {userNameOptionsPermissionGroup, fetchUsersForPermissionGroup} = useUserOptionsGroups()
    
    const {showError} = useError()
    const {timeoutSuccess} = useSuccess()

    //if group details change (new group selected or "add new group" clicked in parent) update state
    useEffect(() => {
        if(groupDetails){
            //set state
            setGroupName(groupDetails.Group_Name)
            setGroupDescription(groupDetails.Description)
            
            //get users in group
            fetchAllUsers()
            fetchUsersForPermissionGroup(groupDetails.GroupID)
            
            return
        }

        //if no selected group, reset state
        setGroupName("")
        setGroupDescription("")
        setUsersAssigned(null)
        setUsersAssignedOriginal(null)

    }, [groupDetails])

    useEffect(() => {
        setUsersAssigned(userNameOptionsPermissionGroup)
        setUsersAssignedOriginal(userNameOptionsPermissionGroup)
    }, [userNameOptionsPermissionGroup])
    
    //helper functions

    //validate fields
    const validate = () => {
        const errors = {}

        if(!groupName){
            errors.groupName = "Group name is required"
        }

        if(!groupDescription){
            errors.groupDescription = "Description is required"
        }

        //if there are errors, set state to errors and return false
        if(Object.keys(errors).length > 0){
            setValidationErrors(errors)
            return false
        }

        setValidationErrors({})

        return true
    }

    //update group details
    const handleUpdateGroup = async() => {
        showLoading("Updating group...")

        const response = await updateGroupDetailsAPI(groupDetails.GroupID, {
            Group_Name: groupName,
            Description: groupDescription
        })
                        
        if(!response.success){
            hideLoading()

            showError(`Error updating group: ${response.message}`)

            return
        }

        hideLoading()

        timeoutSuccess("Group saved")
    }

    //add new group
    const handleAddGroup = async() => {
        showLoading("Adding group...")

        if(!validate()){
            hideLoading()
            showError("Could not add group - some form fields are missing")
            return
        }

        const response = await createPermissionGroupAPI({
            Group_Name: groupName,
            Description: groupDescription
        })
                        
        if(!response.success){
            hideLoading()

            showError(`Error adding group: ${response.message}`)

            return
        }

        const newGroup = response.data

        const newGroupNameOption = {
            value: newGroup.GroupID, 
            label: newGroup.Group_Name
        }
        
        //change selected group to new group
        setSelectedGroup(newGroupNameOption)

        //add new (formatted) group to groupNameOptions
        setGroupNameOptions(prevData => {
            const newData = [...prevData, newGroupNameOption];

            return newData;
        })

        setIsAddNewGroup(false)

        hideLoading()

        timeoutSuccess("Group saved")
    }

    //update users in group
    const handleUpdateGroupUsers = async() => {
        showLoading("Submitting...")

        // get ids from current and original groups
        const usersAssignedIds = usersAssigned.map(user => user.value);
        const usersAssignedOriginalIds = usersAssignedOriginal.map(user => user.value);
    
        // compare ids between current and original user groups to determine which to add and remove
        const usersToAdd = usersAssigned.filter(user => !usersAssignedOriginalIds.includes(user.value));
    
        const usersToRemove = usersAssignedOriginal.filter(user => !usersAssignedIds.includes(user.value));

        //loop through each and call api
        if(usersToAdd.length > 0){
            for(const user of usersToAdd){
                const response = await assignPermissionGroupAPI(user.value, groupDetails.GroupID)
                    
                if(!response.success){
                    hideLoading()

                    showError(`Error adding users: ${response.message}`)
        
                    return
                }
            }
        }

        if(usersToRemove.length > 0){
            for(const user of usersToRemove){
                const response = await removePermissionGroupAPI(user.value, groupDetails.GroupID)
                    
                if(!response.success){
                    hideLoading()

                    showError(`Error removing users: ${response.message}`)
        
                    return
                }
            }
        }

        //update original state
        setUsersAssignedOriginal(usersAssigned)
        
        hideLoading()
        
        //everything submitted successfully, so let user know
        timeoutSuccess("Saved")
    }

    return (

        <div className='flex flex-col'>
            {!isAddNewGroup ? (
                <hr className='mb-12 mt-2'/>
            ) : null}

            {/* group name */}
            <InputContainer>
                <Label htmlFor='groupName' className='mb-2 font-semibold'>Group name</Label>
                <Input name="groupName" value={groupName} onChange={(e)=>setGroupName(e.target.value)} error={validationErrors.groupName} />
            </InputContainer>

            {/* group description */}
            <InputContainer>
                <Label htmlFor='groupDescription' className='mb-2 font-semibold'>Group description</Label>
                <TextInput name="groupDescription" value={groupDescription} onChange={(e)=>setGroupDescription(e.target.value)} error={validationErrors.groupDescription} />
            </InputContainer>

            {/* change button text and handler depending on if user is adding or updating a group */}
            {
                isAddNewGroup 
                ? (
                    <div>
                        <Button variant="success" onClick={() => handleAddGroup()} className='mr-2'>Add Group</Button>
                        <Button variant="cancel" onClick={() => setIsAddNewGroup(false)}>Cancel</Button>
                    </div>
                ) 
                : (
                    <div>
                        <Button variant="success" onClick={() => handleUpdateGroup()}>Update Group Details</Button>
                    </div>
                )
            }

            {/* users in current group */}
            {
                !isAddNewGroup 
                ? (
                    <div className="my-10">
                        <hr className='mb-10 mt-2'/>

                        <Label>Users in group</Label>
                        <SelectContainer>
                            <Select
                                value={usersAssigned}
                                options={userNameOptions}
                                name="usersGroupDropdown"
                                onChange={(x) => setUsersAssigned(x)}
                                menuPosition='fixed'
                                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                isMulti={true}
                            />
                        </SelectContainer>
                        
                        <div>
                            <Button variant="success" onClick={() => handleUpdateGroupUsers()}>Update Group Users</Button>
                        </div>
                        

                    </div>
                )
                : null
            }

        </div>

    )
}